/**
 * AuthProvider — abstracts over MSAL and the local dev mock-auth flow so
 * components can just ask for `useAuth()` without caring which path is active.
 *
 * When env.MOCK_AUTH=true, the provider stores the selected mock role in
 * localStorage and signs X-Mock-User into outbound API requests.
 *
 * When env.MOCK_AUTH=false, it delegates to @azure/msal-react for real MSAL.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { ensureMsalInitialized, loginRequest, msalInstance } from '@/lib/msal';
import { env } from '@/lib/env';
import { setAuthFailureHandler, setAuthHeaderProvider } from '@/lib/api';

export type MockRole = 'supervisor' | 'pm' | 'crew' | 'sales' | 'admin';

interface AuthContextValue {
  isAuthenticated: boolean;
  isInitializing: boolean;
  mode: 'mock' | 'msal';
  // Mock-only
  mockRole: MockRole;
  setMockRole: (role: MockRole) => void;
  // Either mode
  signIn: () => Promise<void>;
  /** Complete an email one-time-code sign-in by storing its session token. */
  signInWithOtp: (token: string) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const MOCK_ROLE_KEY = 'vari-crew.mock-role';
const OTP_SESSION_KEY = 'vari-crew.otp-session';
/** Set (sessionStorage) when we drop a dead OTP session, so the login page can
 * say why the user is back there and open straight onto the code flow. */
const OTP_EXPIRED_NOTICE_KEY = 'vari-crew.otp-expired-notice';

/**
 * Read-and-clear the "your OTP session expired" notice. One-shot so a later,
 * unrelated visit to /login doesn't keep showing it.
 */
export function consumeOtpExpiredNotice(): boolean {
  if (typeof window === 'undefined') return false;
  const had = sessionStorage.getItem(OTP_EXPIRED_NOTICE_KEY) === '1';
  if (had) sessionStorage.removeItem(OTP_EXPIRED_NOTICE_KEY);
  return had;
}

/** Reject if a promise hasn't settled within `ms` — so a stalled MSAL token
 * call (e.g. blocked by Safari/iPad ITP) can never block a request forever. */
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('token acquisition timed out')), ms),
    ),
  ]);
}

// Wire the mock auth header provider eagerly at module load so the very
// first /me query (and any other request before MockAuthProvider's effects
// run) already carries the correct X-Mock-User header. Reading localStorage
// inside the closure means each call picks up the latest role without
// needing a re-set.
if (typeof window !== 'undefined' && env.MOCK_AUTH) {
  setAuthHeaderProvider(() => {
    const role = (localStorage.getItem(MOCK_ROLE_KEY) ?? 'supervisor') as MockRole;
    return { 'X-Mock-User': role };
  });
}

// Real MSAL mode: wire the bearer-token provider eagerly at module load too,
// reading the account live from the msalInstance singleton each call. Doing
// this in a React effect (as before) raced the first /me fetch — React flushes
// child query effects before the parent AuthProvider effect, so the very first
// request after a page refresh went out with no token ("missing bearer token").
if (typeof window !== 'undefined' && !env.MOCK_AUTH) {
  setAuthHeaderProvider(async (): Promise<Record<string, string>> => {
    // OTP session (email one-time-code) takes precedence when present — it's the
    // personal-device path the user explicitly chose over Entra at sign-in.
    const otp = localStorage.getItem(OTP_SESSION_KEY);
    if (otp) return { Authorization: `Bearer ${otp}` };
    await ensureMsalInitialized();
    const account =
      msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) return {};
    try {
      // acquireTokenSilent uses the cached refresh token (no iframe) and renews
      // the access token. We deliberately do NOT fall back to ssoSilent: its
      // hidden iframe is blocked by Safari/iPad ITP and can hang forever, which
      // froze the app at the loading state right after login (apiCall awaits
      // this provider before issuing /me). Cap it with a timeout so a stalled
      // token call can never block the first request indefinitely.
      const result = await withTimeout(
        msalInstance.acquireTokenSilent({ ...loginRequest, account }),
        8000,
      );
      return { Authorization: `Bearer ${result.accessToken}` };
    } catch (err) {
      // Token genuinely needs interaction (expired RT, revoked session): kick a
      // full-page redirect login. For any other/transient error, return no
      // header so the request proceeds (and a 401 surfaces) rather than hanging.
      if (err instanceof InteractionRequiredAuthError) {
        await msalInstance.acquireTokenRedirect(loginRequest);
      }
      return {};
    }
  });
}

function MockAuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [mockRole, setMockRoleState] = useState<MockRole>(() => {
    const stored = localStorage.getItem(MOCK_ROLE_KEY);
    return (stored as MockRole) ?? 'supervisor';
  });

  const setMockRole = useCallback(
    (role: MockRole) => {
      localStorage.setItem(MOCK_ROLE_KEY, role);
      setMockRoleState(role);
      // Invalidate every cached query so identity-dependent data
      // (/me, /projects filtered by role, etc.) refetches with the new header.
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const signIn = useCallback(async () => {
    // No-op for mock; "always signed in"
  }, []);

  const signOut = useCallback(async () => {
    setMockRole('supervisor');
  }, [setMockRole]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: true,
      isInitializing: false,
      mode: 'mock',
      mockRole,
      setMockRole,
      signIn,
      signInWithOtp: () => undefined,
      signOut,
    }),
    [mockRole, setMockRole, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function RealAuthProviderInner({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const queryClient = useQueryClient();
  // An active OTP (email one-time-code) session is an alternative to MSAL.
  const [otpToken, setOtpToken] = useState<string | null>(() =>
    typeof window !== 'undefined' ? localStorage.getItem(OTP_SESSION_KEY) : null,
  );
  const isAuthenticated = accounts.length > 0 || !!otpToken;
  const isInitializing = inProgress !== 'none' && accounts.length === 0 && !otpToken;

  // Keep MSAL's active account in sync so the module-level auth-header provider
  // (registered at load) can resolve a token without depending on React state.
  useEffect(() => {
    if (accounts[0] && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [instance, accounts]);

  // Dead OTP session (the API said OTP_SESSION_EXPIRED — its 30-day TTL ran out,
  // or it was invalidated). Drop the token so `isAuthenticated` falls back to
  // MSAL (if an account exists) or to false → RequireAuth routes to /login —
  // instead of the old dead end: token kept, /me 401s forever, AccessGate shows
  // a Retry that can never succeed. Only this code triggers it; see lib/api.ts.
  // Registering in an effect is safe: the failing response can't arrive before
  // this parent effect has flushed (the fetch is async), unlike the header
  // provider which must be wired at module load for the very first request.
  useEffect(() => {
    setAuthFailureHandler(() => {
      const hadOtp = !!localStorage.getItem(OTP_SESSION_KEY);
      if (!hadOtp) return;
      localStorage.removeItem(OTP_SESSION_KEY);
      sessionStorage.setItem(OTP_EXPIRED_NOTICE_KEY, '1');
      setOtpToken(null);
      // Let the failing request settle before wiping the cache (we're invoked
      // mid-throw), then drop identity-scoped data as signOut does for OTP.
      setTimeout(() => queryClient.clear(), 0);
    });
    return () => setAuthFailureHandler(() => undefined);
  }, [queryClient]);

  const signIn = useCallback(async () => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const signInWithOtp = useCallback(
    (token: string) => {
      localStorage.setItem(OTP_SESSION_KEY, token);
      setOtpToken(token);
      // We're authenticated now — refetch identity-dependent data (/me etc.).
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    const hadOtp = !!localStorage.getItem(OTP_SESSION_KEY);
    localStorage.removeItem(OTP_SESSION_KEY);
    setOtpToken(null);
    // A real MSAL account → full redirect logout. An OTP-only session → just drop
    // the token + clear cached data (no Microsoft round-trip).
    if (accounts.length > 0) {
      await instance.logoutRedirect();
      return;
    }
    if (hadOtp) queryClient.clear();
  }, [instance, accounts, queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isInitializing,
      mode: 'msal',
      mockRole: 'supervisor',
      setMockRole: () => undefined,
      signIn,
      signInWithOtp,
      signOut,
    }),
    [isAuthenticated, isInitializing, signIn, signInWithOtp, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function RealAuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    void ensureMsalInitialized().then(() => setReady(true));
  }, []);
  if (!ready) return null;
  return (
    <MsalProvider instance={msalInstance}>
      <RealAuthProviderInner>{children}</RealAuthProviderInner>
    </MsalProvider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return env.MOCK_AUTH ? (
    <MockAuthProvider>{children}</MockAuthProvider>
  ) : (
    <RealAuthProvider>{children}</RealAuthProvider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
